import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DigitalSignature = ({ onComplete, onCancel, treatmentPlanName }) => {
  const sigCanvas = useRef(null);
  const [signatureMethod, setSignatureMethod] = useState('draw'); // draw, type
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  const handleClear = () => {
    if (sigCanvas?.current) {
      sigCanvas?.current?.clear();
    }
  };

  const handleSave = () => {
    if (!agreed) {
      alert('Please confirm that you agree to the terms before signing.');
      return;
    }

    let signatureData = '';

    if (signatureMethod === 'draw') {
      if (sigCanvas?.current && sigCanvas?.current?.isEmpty()) {
        alert('Please provide your signature before continuing.');
        return;
      }
      signatureData = sigCanvas?.current?.toDataURL();
    } else {
      if (!typedName?.trim()) {
        alert('Please type your full name before continuing.');
        return;
      }
      signatureData = `TYPED:${typedName}`;
    }

    onComplete(signatureData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-1">Digital Signature</h2>
              <p className="text-sm text-muted-foreground">{treatmentPlanName}</p>
            </div>
            <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Signature Method Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">Signature Method</label>
            <div className="flex gap-3">
              <button
                onClick={() => setSignatureMethod('draw')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  signatureMethod === 'draw' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                <Icon name="Pen" size={18} />
                <span className="font-medium">Draw Signature</span>
              </button>
              <button
                onClick={() => setSignatureMethod('type')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  signatureMethod === 'type' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                <Icon name="Type" size={18} />
                <span className="font-medium">Type Name</span>
              </button>
            </div>
          </div>

          {/* Signature Input Area */}
          <div className="mb-6">
            {signatureMethod === 'draw' ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Draw Your Signature</label>
                <div className="border-2 border-dashed border-border rounded-lg bg-background">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: 'w-full h-48 rounded-lg',
                      style: { touchAction: 'none' }
                    }}
                    backgroundColor="rgb(255, 255, 255)"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button variant="ghost" size="sm" iconName="RotateCcw" onClick={handleClear}>
                    Clear
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Type Your Full Legal Name</label>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e?.target?.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {typedName && (
                  <div className="mt-4 p-4 bg-background border border-border rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Signature Preview:</p>
                    <p className="text-3xl font-signature text-foreground italic">{typedName}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Consent Agreement */}
          <div className="bg-muted/50 rounded-lg border border-border p-4 mb-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="signature-agreement"
                checked={agreed}
                onChange={(e) => setAgreed(e?.target?.checked)}
                className="mt-1 w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="signature-agreement" className="text-sm text-foreground leading-relaxed cursor-pointer">
                I confirm that I am the patient (or authorized representative) and that I have read, understood, and agree to the treatment plan as described. I understand that this digital signature is legally binding and has the same effect as a handwritten signature. I consent to the proposed treatment and authorize my provider to proceed.
              </label>
            </div>
          </div>

          {/* Signature Details */}
          <div className="bg-background rounded-lg border border-border p-4 mb-6">
            <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Shield" size={18} className="text-primary" />
              Signature Verification Details
            </h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Timestamp:</span>
                <span className="text-foreground font-medium">{new Date()?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>IP Address:</span>
                <span className="text-foreground font-medium">Recorded</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Identity Verification:</span>
                <span className="text-success font-medium flex items-center gap-1">
                  <Icon name="CheckCircle" size={14} />
                  Authenticated
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Document Security:</span>
                <span className="text-success font-medium flex items-center gap-1">
                  <Icon name="Lock" size={14} />
                  Encrypted
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="lg" 
              iconName="CheckCircle" 
              onClick={handleSave}
              disabled={!agreed}
              className="flex-1"
            >
              Complete Signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalSignature;